import { getEffectiveResponse, SHOW_WORKER_RESPONSE_STATUS } from '../schedulingRules'
import type { Job, JobWorker, WorkerResponse } from '../types'

/** Offers that still hold a place on the job (declined/expired ones do not). */
export function isActiveResponse(response: WorkerResponse): boolean {
  return response === 'pending' || response === 'accepted'
}

/** Active workers; pass `now` to also treat stale pending offers as expired. */
export function getActiveJobWorkers(job: Job, now?: number): JobWorker[] {
  return job.workers.filter((worker) => isActiveResponse(now == null ? worker.response : getEffectiveResponse(worker, now)))
}

/**
 * Workers listed on the card / popup: everyone offered the job with the response UI on,
 * otherwise only the ones actually assigned (declined / expired offers are hidden).
 */
export function getVisibleJobWorkers(job: Job, now?: number): JobWorker[] {
  return SHOW_WORKER_RESPONSE_STATUS ? job.workers : getActiveJobWorkers(job, now)
}

export function getActiveWorkerIds(job: Job, now?: number): string[] {
  return getActiveJobWorkers(job, now).map((worker) => worker.workerId)
}

export function jobHasWorker(job: Job, workerId: string): boolean {
  return job.workers.some((worker) => worker.workerId === workerId)
}

export function getStaffing(job: Job, now?: number): { assigned: number; required: number } {
  return { assigned: getActiveJobWorkers(job, now).length, required: Math.max(1, job.requiredWorkers || 1) }
}

/** Jobs whose assignments are still loading are never counted as unassigned / understaffed. */
export function isJobUnassigned(job: Job, now?: number): boolean {
  if (job.staffingPending) return false
  return job.status !== 'completed' && getActiveJobWorkers(job, now).length === 0
}

export function isJobUnderstaffed(job: Job, now?: number): boolean {
  if (job.staffingPending) return false
  const { assigned, required } = getStaffing(job, now)
  return job.status !== 'completed' && assigned < required
}

/** Overlapping, not-completed jobs the worker is actively on. */
export function findWorkerConflicts(candidate: Job, workerId: string, jobs: Job[]): Job[] {
  const start = Date.parse(candidate.scheduledStart)
  const end = Date.parse(candidate.scheduledEnd)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return []
  return jobs.filter((job) => {
    if (job.id === candidate.id || job.status === 'completed') return false
    if (!getActiveWorkerIds(job).includes(workerId)) return false
    const otherStart = Date.parse(job.scheduledStart)
    const otherEnd = Date.parse(job.scheduledEnd)
    return Number.isFinite(otherStart) && Number.isFinite(otherEnd) && otherStart < end && start < otherEnd
  })
}
